import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { isPlatformBrowser, NgClass } from '@angular/common';

import { SwiperOptions } from 'swiper/types';

import { AssetFormatterPipe } from '@pipes/asset-formatter-pipe';

type CarouselType =
  | 'authors'
  | 'tags'
  | 'hindsight'
  | 'videos'
  | 'videos-detail'
  | 'interest';

@Component({
  selector: 'app-carousel',
  imports: [NgClass, RouterLink, AssetFormatterPipe],
  templateUrl: './carousel.html',
  styleUrl: './carousel.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class Carousel {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);

  items = input<any[]>([]);
  duplicate = input<boolean>(true);
  carouselType = input<CarouselType>('authors');

  navigationEnabled = signal<boolean>(true);
  leftNavigationEnabled = signal<boolean>(true);
  rightNavigationEnabled = signal<boolean>(true);
  navigation = signal(false);

  carouselItems = computed(() => {
    const items = this.items();

    if (this.duplicate()) {
      return [...items, ...items];
    }

    return [...items];
  });

  swiper = viewChild<ElementRef<any>>('swiper');

  private width = signal(0);
  initialized = signal(false);
  isMobile = computed(() => this.width() < 768);

  private resizeListener = () => {
    if (isPlatformBrowser(this.platformId)) {
      this.width.set(window.innerWidth);
    }
  };

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.width.set(window.innerWidth);
      window.addEventListener('resize', this.resizeListener);

      this.initialized.set(true);
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  handleItemAction(id: string) {
    this.router.navigate([`/authors/${id}`]);
  }

  move(isNext: boolean) {
    const swiperEl = this.swiper()?.nativeElement;

    if (isNext) {
      swiperEl.swiper?.slideNext();
    } else {
      swiperEl.swiper?.slidePrev();
    }
  }

  handleSwiper() {
    const swiperEl = this.swiper()?.nativeElement;

    if (!swiperEl) return;

    const config: SwiperOptions = this.getCarouselConfig();
    Object.assign(swiperEl, config);
    swiperEl.initialize();
  }
  getCarouselConfig(): SwiperOptions {
    const delay = Math.random() * 1000 + 3000;

    const config: SwiperOptions = {
      loop: true,
      navigation: false,
      spaceBetween: 20,
      pagination: false,
      centerInsufficientSlides: true,
      autoplay: { delay, disableOnInteraction: false },
      autoHeight: true,
    };

    config['autoplay'] = false;

    this.navigationEnabled.set(false);

    switch (this.carouselType()) {
      case 'authors': {
        config['breakpoints'] = {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          480: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          768: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
          1200: {
            slidesPerView: 4,
            slidesPerGroup: 4,
          },
        };

        config['pagination'] = {
          type: 'bullets',
          clickable: true,
        };

        break;
      }

      case 'tags': {
        config['breakpoints'] = {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          480: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          768: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
        };

        config['pagination'] = {
          type: 'bullets',
          clickable: true,
        };

        break;
      }

      case 'videos': {
        config['breakpoints'] = {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          480: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          768: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
        };

        break;
      }

      case 'hindsight': {
        config['breakpoints'] = {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          480: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          768: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
          912: {
            slidesPerView: 4,
            slidesPerGroup: 4,
          },
        };

        config['pagination'] = {
          type: 'bullets',
          clickable: true,
        };

        break;
      }

      default: {
        config['breakpoints'] = {
          0: {
            slidesPerView: 1,
          },
          640: {
            slidesPerView: 2,
          },
          768: {
            slidesPerView: 2,
          },
          992: {
            slidesPerView: 3,
          },
          1200: {
            slidesPerView: 4,
          },
        };

        break;
      }
    }

    return config;
  }
}
